import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { ViewportCameraKey } from "building-types/views/CameraKey";
import type { DocViewType } from "building-types/views/GeneratedPageView";
import type { DocEntityType } from "../doc/docEntityTypes";

export type ParameterValue = number | string | DocEntityId<ParameterDocEntityType> | boolean;

export interface PrimitiveValue {
  readonly type: "PRIMITIVE";
  readonly value: ParameterValue;
}

interface ParameterReferenceValueBase<T extends DocEntityType> {
  readonly type: T;
  readonly value: DocEntityId<T>;
}

export interface OptionPresetValue extends ParameterReferenceValueBase<"PRESET"> {}
export interface OptionValue extends ParameterReferenceValueBase<"OPTION"> {}
export interface LevelValue {
  readonly type: "LEVEL";
  readonly value: DocEntityId<"LEVEL" | "COMPOSITE_LEVEL">;
}
export interface UnitValue {
  readonly type: "UNIT";
  readonly value: DocEntityId<"LABEL">;
}
export interface DataSequenceValue extends ParameterReferenceValueBase<"DATA_SEQUENCE"> {}
export interface ViewTemplateValue extends ParameterReferenceValueBase<"VIEW_TEMPLATE"> {}
export interface ContentTemplateValue extends ParameterReferenceValueBase<"CONTENT_TEMPLATE"> {}
export interface ElevationViewValue extends ParameterReferenceValueBase<"ELEVATION_VIEW"> {}
export interface SectionViewValue extends ParameterReferenceValueBase<"SECTION_VIEW"> {}

/**
 * Normalizing the parameter value interface allows for better reference detection via the doc schema.
 */
export type ParameterValueHandle =
  | PrimitiveValue
  | OptionPresetValue
  | OptionValue
  | LevelValue
  | UnitValue
  | DataSequenceValue
  | ViewTemplateValue
  | ContentTemplateValue
  | ElevationViewValue
  | SectionViewValue;

/**
 * Parameters allow certain entity types to be driven with user-specified parameters.
 * The parameter is defined on a parent entity (ie ContentTemplates and PlanSetTemplates)
 * and then can be referenced by direct child entities (ParameterRef). The parameter
 * values are then substituted into the child entities when generating the entity.
 */
interface ParameterBase<T extends ParameterType> {
  readonly type: T;
}

export interface NumberParameter extends ParameterBase<ParameterType.NUMBER> {
  readonly defaultValue?: number;
}

export interface BooleanParameter extends ParameterBase<ParameterType.BOOLEAN> {
  readonly defaultValue?: boolean;
}

export interface DrawingScaleParameter extends ParameterBase<ParameterType.DRAWING_SCALE> {
  // TODO: this should be BuiltinViewScaleFactor
  readonly defaultValue?: number;
}

export interface FootInchParameter extends ParameterBase<ParameterType.FOOT_INCH> {
  readonly defaultValue?: number;
}

export interface TextParameter extends ParameterBase<ParameterType.TEXT> {
  readonly defaultValue?: string;
}

export interface UrlParameter extends ParameterBase<ParameterType.URL> {
  readonly defaultValue?: string;
}

export interface OptionPresetParameter extends ParameterBase<ParameterType.PRESET> {
  readonly defaultValue?: DocEntityId<"PRESET">;
}

export interface DataSequenceParameter extends ParameterBase<ParameterType.DATA_SEQUENCE> {
  readonly defaultValue?: DocEntityId<"DATA_SEQUENCE">;
}

export interface LevelParameter extends ParameterBase<ParameterType.LEVEL> {
  readonly defaultValue?: DocEntityId<"LEVEL" | "COMPOSITE_LEVEL">;
}

export interface UnitParameter extends ParameterBase<ParameterType.UNIT> {
  readonly defaultValue?: DocEntityId<"LABEL">;
}

export interface ElevationViewParameter extends ParameterBase<ParameterType.ELEVATION_VIEW> {
  readonly defaultValue?: DocEntityId<"ELEVATION_VIEW">;
}

export interface SectionViewParameter extends ParameterBase<ParameterType.SECTION_VIEW> {
  readonly defaultValue?: DocEntityId<"SECTION_VIEW">;
}

export interface View3DParameter extends ParameterBase<ParameterType.VIEW_3D> {
  readonly defaultValue?: ViewportCameraKey;
}

export interface DrawingTemplateParameter extends ParameterBase<ParameterType.DRAWING_TEMPLATE> {
  readonly defaultValue?: DocEntityId<"CONTENT_TEMPLATE">;
}

export interface ViewTemplateParameter extends ParameterBase<ParameterType.VIEW_TEMPLATE> {
  readonly viewType: DocViewType;
  readonly defaultValue?: DocEntityId<"VIEW_TEMPLATE">;
}

export interface OptionChoiceParameter extends ParameterBase<ParameterType.OPTION> {
  readonly optionSetId: DocEntityId<"OPTION_SET">;
  readonly defaultValue?: DocEntityId<"OPTION">;
}

export type ParameterDefinition =
  | NumberParameter
  | BooleanParameter
  | DrawingScaleParameter
  | FootInchParameter
  | TextParameter
  | UrlParameter
  | LevelParameter
  | UnitParameter
  | ElevationViewParameter
  | SectionViewParameter
  | View3DParameter
  | DrawingTemplateParameter
  | ViewTemplateParameter
  | OptionChoiceParameter
  | OptionPresetParameter
  | DataSequenceParameter;

/**
 * Local parameters are user-defined on a content template and are available to the content template's children.
 */
export type LocalParameter = ParameterDefinition &
  DocEntityBase<"LOCAL_PARAMETER"> & {
    readonly id: PrimaryEntityId<"LOCAL_PARAMETER">;
    readonly parentId: DocEntityParentId<"CONTENT_TEMPLATE" | "PLAN_SET_TEMPLATE">;
  };

/**
 * Global parameters are user-defined and available to any content template.
 */
export type GlobalParameter = ParameterDefinition &
  DocEntityBase<"GLOBAL_PARAMETER"> & {
    readonly id: PrimaryEntityId<"GLOBAL_PARAMETER">;
    readonly parentId?: never;
  };

/**
 * System parameters are generated from information on the doc, but not stored there themselves.
 */
export type SystemParameter = Extract<
  ParameterDefinition,
  NumberParameter | TextParameter | OptionChoiceParameter | OptionPresetParameter | UrlParameter
> &
  DocEntityBase<"SYSTEM_PARAMETER"> & {
    readonly parentId?: never;
  };

export type Parameter = LocalParameter | GlobalParameter | SystemParameter;

export enum ParameterType {
  NUMBER = "NUMBER",
  BOOLEAN = "BOOLEAN",
  DRAWING_SCALE = "DRAWING_SCALE",
  FOOT_INCH = "FOOT_INCH",
  TEXT = "TEXT",
  URL = "URL",
  LEVEL = "LEVEL",
  UNIT = "UNIT",
  ELEVATION_VIEW = "ELEVATION_VIEW",
  SECTION_VIEW = "SECTION_VIEW",
  VIEW_3D = "VIEW_3D",
  DRAWING_TEMPLATE = "DRAWING_TEMPLATE",
  PRESET = "PRESET",
  OPTION = "OPTION",
  VIEW_TEMPLATE = "VIEW_TEMPLATE",
  DATA_SEQUENCE = "DATA_SEQUENCE",
}

/**
 * Type utility that extracts DocEntityType values that are also valid ParameterType values.
 * This represents doc entity types that can be referenced by parameters.
 *
 * Need to manually handle DRAWING_TEMPLATE by adding CONTENT_TEMPLATE to the union.
 */
type ParameterTypeValues = `${ParameterType}`;

export type ParameterDocEntityType =
  | (ParameterTypeValues & DocEntityType)
  | Extract<DocEntityType, "CONTENT_TEMPLATE" | "COMPOSITE_LEVEL" | "LABEL">;

export const entityBasedParameterTypes: {
  [type in ParameterType]: type extends ParameterType.DRAWING_TEMPLATE
    ? Extract<DocEntityType, "CONTENT_TEMPLATE">
    : type | undefined;
} = {
  [ParameterType.LEVEL]: ParameterType.LEVEL,
  [ParameterType.UNIT]: ParameterType.UNIT,
  [ParameterType.DATA_SEQUENCE]: ParameterType.DATA_SEQUENCE,
  DRAWING_TEMPLATE: "CONTENT_TEMPLATE",
  [ParameterType.SECTION_VIEW]: ParameterType.SECTION_VIEW,
  [ParameterType.ELEVATION_VIEW]: ParameterType.ELEVATION_VIEW,
  [ParameterType.VIEW_3D]: ParameterType.VIEW_3D,
  [ParameterType.VIEW_TEMPLATE]: ParameterType.VIEW_TEMPLATE,
  [ParameterType.OPTION]: ParameterType.OPTION,
  [ParameterType.PRESET]: ParameterType.PRESET,
  [ParameterType.NUMBER]: undefined,
  [ParameterType.BOOLEAN]: undefined,
  [ParameterType.DRAWING_SCALE]: undefined,
  [ParameterType.FOOT_INCH]: undefined,
  [ParameterType.TEXT]: undefined,
  [ParameterType.URL]: undefined,
};

/**
 * A reference to a parameter. This is used in entities to reference a parameter defined on a parent entity.
 */
export interface ParameterRef {
  readonly type: "PARAMETER_REF";
  readonly id: DocEntityId<"LOCAL_PARAMETER"> | DocEntityId<"GLOBAL_PARAMETER"> | DocEntityId<"SYSTEM_PARAMETER">;
}

/**
 * Removes the ParameterRef type from an object and replaces it with the value type.
 */
export type RemoveParameterRef<T> = {
  [P in keyof T]: T[P] extends ParameterRef | infer R ? R : T[P];
};

/**
 * Replaces any ParameterValueHandle occurrences with ParameterValue, including within unions.
 */
export type RemoveParameterValueHandle<T> = {
  [P in keyof T]: T[P] extends infer U ? (U extends ParameterValueHandle ? ParameterValue : U) : never;
};
